import { useRef } from 'react'
import Section from '../Section/Section'
import { useIntl, defineMessages } from 'react-intl'
import { Listing } from '../../../../api/gqlaxy/gql/generated/graphql'
import { Box, Text, Link, useTheme } from '@homebotapp/hb-react-component-catalog'
import { formatCityStateZip, formatStreetAddress } from '../../../../helpers/formatListingAddress'

export const MSG = defineMessages({
  loc: {
    id: 'HomeSearch.listingDetails.locationMap',
    defaultMessage: 'Location'
  },
  link: {
    id: 'HomeSearch.listingDetails.mapLink',
    defaultMessage: 'Open Google Maps in new tab'
  }
})

interface LocationMapProps {
  listing?: Listing
  showHeading: boolean
}

export const LocationMap = ({ listing, showHeading }: LocationMapProps) => {
  const intl = useIntl()
  const { theme } = useTheme()
  const mapRef = useRef(null)

  /* https://developers.google.com/maps/documentation/maps-static/styling */
  const mapDarkStyles = `
  &style=feature%3Aall%7Celement%3Ageometry%7Ccolor%3A0x242f3e%7C
  &style=feature%3Aall%7Celement%3Alabels.text.stroke%7Ccolor%3A0x242f3e%7C
  &style=feature%3Aall%7Celement%3Alabels.text.fill%7Ccolor%3A0x746855%7C
  &style=feature%3Aadministrative.locality%7Celement%3Alabels.text.fill%7Ccolor%3A0xd59563%7C
  &style=feature%3Apoi%7Celement%3Alabels.text.fill%7Ccolor%3A0xd59563%7C
  &style=feature%3Apoi.park%7Celement%3Ageometry%7Ccolor%3A0x263c3f%7C
  &style=feature%3Apoi.park%7Celement%3Alabels.text.fill%7Ccolor%3A0x6b9a76%7C
  &style=feature%3Aroad%7Celement%3Ageometry%7Ccolor%3A0x38414e%7C
  &style=feature%3Aroad%7Celement%3Ageometry.stroke%7Ccolor%3A0x212a37%7C
  &style=feature%3Aroad%7Celement%3Alabels.text.fill%7Ccolor%3A0x9ca5b3%7C
  &style=feature%3Aroad.highway%7Celement%3Ageometry%7Ccolor%3A0x746855%7C
  &style=feature%3Aroad.highway%7Celement%3Ageometry.stroke%7Ccolor%3A0x1f2835%7C
  &style=feature%3Aroad.highway%7Celement%3Alabels.text.fill%7Ccolor%3A0xf3d19c%7C
  &style=feature%3Atransit%7Celement%3Ageometry%7Ccolor%3A0x2f3948%7C
  &style=feature%3Atransit.station%7Celement%3Alabels.text.fill%7Ccolor%3A0xd59563%7C
  &style=feature%3Awater%7Celement%3Ageometry%7Ccolor%3A0x17263c%7C
  &style=feature%3Awater%7Celement%3Alabels.text.fill%7Ccolor%3A0x515c6d%7C
  &style=feature%3Awater%7Celement%3Alabels.text.stroke%7Ccolor%3A0x17263c%7C'
`

  const zoom = 15
  const mapSize = '600x250'
  const markerColor = 'blue'
  const label = ''
  const googleApiKey = process.env.REACT_APP_GOOGLE_KEY
  const baseUrl = 'https://maps.googleapis.com/maps/api/staticmap'
  const centerParam = `center=${listing?.address?.latitude},${listing?.address?.longitude}`
  const zoomParam = `zoom=${zoom}`
  const sizeParam = `size=${mapSize}`
  const markerParam = `markers=color:${markerColor}|label:${label}|${listing?.address?.latitude},${listing?.address?.longitude}`
  const themeStyles = theme.isLightThemeType ? '' : mapDarkStyles
  const styleParam = 'style=feature:poi|element:labels|visibility:off'
  const googleMapsLinkUrl =
    listing?.address?.latitude &&
    listing?.address?.longitude &&
    `https://www.google.com/maps/search/?api=1&query=${listing?.address?.latitude}%2C${listing?.address?.longitude}`
  const staticMapURL = `${baseUrl}?${centerParam}&${zoomParam}&${sizeParam}&${markerParam}${themeStyles}&${styleParam}&key=${googleApiKey}`

  return (
    <Section>
      {showHeading && <Section.Heading>{intl.formatMessage(MSG.loc)}</Section.Heading>}
      <>
        <Text mb={0} size='xs' fontWeight='semibold'>
          {formatStreetAddress(listing?.address)}
        </Text>
        <Text size='xs' fontWeight='semibold'>
          {formatCityStateZip(listing?.address)}
        </Text>
        {googleMapsLinkUrl && (
          <>
            <Link size='sm' href={googleMapsLinkUrl} fontWeight='semibold' isExternal>
              {intl.formatMessage(MSG.link)}
            </Link>
            <Box mt={2} id='map-container'>
              <div id='map-wrapper' ref={mapRef}>
                {/* https://developers.google.com/maps/documentation/maps-static */}
                <Link href={googleMapsLinkUrl} isExternal>
                  <img
                    src={staticMapURL}
                    style={{ borderRadius: '8px', width: '100%' }}
                    alt='map showing the location of the listing'
                  ></img>
                </Link>
              </div>
            </Box>
          </>
        )}
      </>
    </Section>
  )
}
